import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, Modal, Table, Spinner, Badge, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { supplierMaterialAPI } from '../../services/purchaseOrder';
import { pickPurchasePackage, packageOptionLabel } from '../../utils/inventoryUnit';
import { parseShopeeLink, shortenShopeeLink } from '../../utils/shopeeLink';

/**
 * SupplierMaterialModal — quản lý danh sách NVL mà 1 NCC cung cấp
 * Props: show, onHide, supplier (object), allMaterials (array with packages)
 */
export default function SupplierMaterialModal({ show, onHide, supplier, allMaterials = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // id of item being saved
  const [addForm, setAddForm] = useState({ material_id: '', package_id: '', default_price: '', shopee_url: '' });
  // #233654 — sửa link Shopee từng dòng: { [supplierMaterialId]: chuỗi đang gõ }
  const [linkDraft, setLinkDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  const fetchItems = async () => {
    if (!supplier?.id) return;
    try {
      setLoading(true);
      const data = await supplierMaterialAPI.list({ supplier_id: supplier.id, is_active: true });
      setItems(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Không thể tải danh sách NVL');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchItems();
      setAddForm({ material_id: '', package_id: '', default_price: '', shopee_url: '' });
      setLinkDraft({});
      setSearch('');
    }
  }, [show, supplier?.id]);

  const existingMaterialIds = useMemo(() => new Set(items.map((i) => i.material_id)), [items]);

  const availableMaterials = useMemo(
    () => allMaterials.filter((m) => !existingMaterialIds.has(m.id) && m.is_active !== false),
    [allMaterials, existingMaterialIds],
  );

  const selectedAddMaterial = useMemo(
    () => allMaterials.find((m) => m.id === addForm.material_id),
    [allMaterials, addForm.material_id],
  );

  const addPackages = selectedAddMaterial?.packages ?? [];

  const handleAddMaterialChange = (materialId) => {
    const mat = allMaterials.find((m) => m.id === materialId);
    const defaultPkg = pickPurchasePackage(mat?.packages);
    setAddForm((prev) => ({ ...prev, material_id: materialId, package_id: defaultPkg?.id ?? '' }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.material_id) { toast.error('Vui lòng chọn NVL'); return; }
    // #233654 — chặn dán nhầm link không phải sản phẩm Shopee ngay tại form thêm
    if (addForm.shopee_url.trim()) {
      const info = parseShopeeLink(addForm.shopee_url);
      if (info.kind !== 'PRODUCT' && info.kind !== 'SHORT') {
        toast.error(info.message || 'Link Shopee không hợp lệ');
        return;
      }
    }
    try {
      setAdding(true);
      await supplierMaterialAPI.create({
        supplier_id: supplier.id,
        material_id: addForm.material_id,
        package_id: addForm.package_id || undefined,
        default_price: addForm.default_price ? Number(addForm.default_price) : undefined,
        shopee_url: addForm.shopee_url.trim() || undefined,
      });
      toast.success('Đã thêm NVL');
      setAddForm({ material_id: '', package_id: '', default_price: '', shopee_url: '' });
      fetchItems();
    } catch (err) {
      toast.error(err?.message || 'Không thể thêm NVL');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (item, field, value) => {
    try {
      setSaving(item.id);
      await supplierMaterialAPI.update(item.id, { [field]: value === '' ? undefined : value });
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, [field]: value } : i));
    } catch (err) {
      toast.error(err?.message || 'Không thể cập nhật');
      fetchItems();
    } finally {
      setSaving(null);
    }
  };

  /**
   * #233654 — lưu/xoá link sản phẩm Shopee của CẶP NVL × NCC này.
   *
   * Link gắn theo cặp (không gắn theo NVL) vì cùng 1 NVL được mua ở nhiều shop khác nhau —
   * dữ liệu thật: 24/43 NVL có từ 2 NCC trở lên.
   * Kiểm định dạng ở đây chỉ để báo NGAY cho người nhập; server vẫn tự kiểm lại.
   */
  const handleSaveLink = async (item) => {
    const raw = linkDraft[item.id] ?? '';
    const trimmed = raw.trim();

    if (trimmed) {
      const info = parseShopeeLink(trimmed);
      if (info.kind !== 'PRODUCT' && info.kind !== 'SHORT') {
        toast.error(info.message || 'Link Shopee không hợp lệ');
        return;
      }
      if (info.kind === 'SHORT') toast.info(info.message);
    }

    try {
      setSaving(item.id);
      const saved = await supplierMaterialAPI.update(item.id, { shopee_url: trimmed });
      setItems((prev) => prev.map((i) => (i.id === item.id
        ? {
          ...i,
          shopee_url: saved?.shopee_url ?? (trimmed ? trimmed : null),
          shopee_shop_id: saved?.shopee_shop_id ?? null,
          shopee_item_id: saved?.shopee_item_id ?? null,
        }
        : i)));
      setLinkDraft((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
      toast.success(trimmed ? 'Đã lưu link Shopee' : 'Đã xoá link Shopee');
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu link');
      fetchItems();
    } finally {
      setSaving(null);
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Xóa "${item.material?.name}" khỏi danh sách NCC này?`)) return;
    try {
      await supplierMaterialAPI.remove(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success('Đã xóa');
    } catch (err) {
      toast.error(err?.message || 'Không thể xóa');
    }
  };

  const filtered = items.filter((i) =>
    !search || i.material?.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const fmtPrice = (v) => v != null ? Number(v).toLocaleString('vi-VN') : '—';

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>NVL của NCC: <span className="text-primary">{supplier?.name}</span></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Form thêm NVL mới */}
        <Form onSubmit={handleAdd} className="border rounded p-3 mb-4 bg-light">
          <h6 className="mb-3">Thêm NVL mới</h6>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <Form.Label className="small mb-1">NVL <span className="text-danger">*</span></Form.Label>
              <Form.Select
                size="sm"
                value={addForm.material_id}
                onChange={(e) => handleAddMaterialChange(e.target.value)}
              >
                <option value="">-- Chọn NVL --</option>
                {availableMaterials.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="small mb-1">Quy cách mặc định</Form.Label>
              <Form.Select
                size="sm"
                value={addForm.package_id}
                onChange={(e) => setAddForm((p) => ({ ...p, package_id: e.target.value }))}
                disabled={!addPackages.length}
              >
                <option value="">-- Chọn quy cách --</option>
                {addPackages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {packageOptionLabel(p, selectedAddMaterial?.unit)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="small mb-1">Giá mặc định (VNĐ)</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min="0"
                placeholder="0"
                value={addForm.default_price}
                onChange={(e) => setAddForm((p) => ({ ...p, default_price: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <Button type="submit" variant="success" size="sm" disabled={adding} className="w-100">
                {adding ? <Spinner size="sm" /> : '+ Thêm'}
              </Button>
            </div>
            <div className="col-12">
              <Form.Label className="small mb-1">
                Link sản phẩm Shopee <span className="text-muted">(không bắt buộc — gắn sau cũng được)</span>
              </Form.Label>
              <Form.Control
                size="sm"
                type="url"
                inputMode="url"
                placeholder="https://shopee.vn/ten-san-pham-i.<mã shop>.<mã sp>"
                value={addForm.shopee_url}
                isInvalid={!!addForm.shopee_url.trim() && !['PRODUCT', 'SHORT'].includes(parseShopeeLink(addForm.shopee_url).kind)}
                onChange={(e) => setAddForm((p) => ({ ...p, shopee_url: e.target.value }))}
              />
              {!!addForm.shopee_url.trim() && (
                <Form.Text
                  className={['PRODUCT', 'SHORT'].includes(parseShopeeLink(addForm.shopee_url).kind)
                    ? 'text-muted' : 'text-danger'}
                  style={{ fontSize: 11 }}
                >
                  {parseShopeeLink(addForm.shopee_url).message || '✓ Link sản phẩm hợp lệ'}
                </Form.Text>
              )}
            </div>
          </div>
        </Form>

        {/* Search */}
        <div className="mb-3">
          <Form.Control
            size="sm"
            placeholder="Tìm NVL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 260 }}
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-4"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <p className="text-muted text-center py-3">Chưa có NVL nào. Thêm NVL ở trên.</p>
        ) : (
          <Table bordered hover responsive size="sm">
            <thead className="table-light">
              <tr>
                <th>NVL</th>
                <th>Đơn vị</th>
                <th style={{ width: 180 }}>Quy cách mặc định</th>
                <th style={{ width: 160 }}>Giá mặc định (VNĐ)</th>
                <th style={{ minWidth: 260 }}>
                  Link sản phẩm Shopee
                  <div className="text-muted fw-normal" style={{ fontSize: 11 }}>
                    Mở đúng sản phẩm trên Shopee → Chia sẻ → Sao chép liên kết
                  </div>
                </th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td className="fw-medium">{item.material?.name}</td>
                  <td className="text-muted">{item.material?.unit}</td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={item.package_id ?? ''}
                      onChange={(e) => handleUpdate(item, 'package_id', e.target.value || null)}
                      disabled={saving === item.id}
                    >
                      <option value="">-- Không chọn --</option>
                      {(allMaterials.find((m) => m.id === item.material_id)?.packages ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {packageOptionLabel(p, item.material?.unit)}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      min="0"
                      defaultValue={item.default_price ?? ''}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (v !== String(item.default_price ?? '')) {
                          handleUpdate(item, 'default_price', v ? Number(v) : null);
                        }
                      }}
                      disabled={saving === item.id}
                    />
                  </td>
                  <td>
                    {(() => {
                      const draft = linkDraft[item.id];
                      const editing = draft !== undefined;
                      const current = item.shopee_url ?? '';
                      const info = editing && draft.trim() ? parseShopeeLink(draft) : null;
                      const bad = info && info.kind !== 'PRODUCT' && info.kind !== 'SHORT';

                      return (
                        <>
                          <InputGroup size="sm">
                            <Form.Control
                              size="sm"
                              type="url"
                              inputMode="url"
                              placeholder="https://shopee.vn/...-i.<shop>.<sp>"
                              value={editing ? draft : current}
                              isInvalid={!!bad}
                              onChange={(e) => setLinkDraft((prev) => ({ ...prev, [item.id]: e.target.value }))}
                              disabled={saving === item.id}
                            />
                            {editing && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleSaveLink(item)}
                                disabled={saving === item.id || !!bad}
                              >Lưu</Button>
                            )}
                            {!editing && current && (
                              <>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  href={current}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Mở sản phẩm trên Shopee"
                                >Mở</Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  title="Xoá link"
                                  onClick={() => setLinkDraft((prev) => ({ ...prev, [item.id]: '' }))}
                                  disabled={saving === item.id}
                                >✕</Button>
                              </>
                            )}
                          </InputGroup>

                          {bad && (
                            <Form.Text className="text-danger d-block" style={{ fontSize: 11 }}>
                              {info.message}
                            </Form.Text>
                          )}
                          {!bad && info?.kind === 'SHORT' && (
                            <Form.Text className="text-warning d-block" style={{ fontSize: 11 }}>
                              {info.message}
                            </Form.Text>
                          )}
                          {!editing && current && (
                            <Form.Text className="text-muted d-block text-truncate" style={{ fontSize: 11 }}>
                              {item.shopee_item_id
                                ? `Mã SP ${item.shopee_item_id} · shop ${item.shopee_shop_id}`
                                : shortenShopeeLink(current)}
                            </Form.Text>
                          )}
                          {!editing && !current && (
                            <Form.Text className="text-muted d-block" style={{ fontSize: 11 }}>
                              Chưa gắn link — NVL này sẽ nằm ở nhóm "Chưa gắn link" khi đi chợ.
                            </Form.Text>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemove(item)}
                      disabled={saving === item.id}
                    >✕</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Đóng</Button>
      </Modal.Footer>
    </Modal>
  );
}
